// auth.ts

import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useRouter } from 'vue-router'
  
export const useAuthStore = defineStore('auth', () => {
  const username = ref('')
  const isAuthenticated = ref(false)
  const authToken = ref('')
  const csrfToken = ref('')
  const loginDialogVisible = ref(false)
  const redirectOnSignIn = ref('')
  const router = useRouter()
  const isInitialized = ref(false)

  const setUsername = (name: string) => {
    username.value = name
  }

  const setAuthenticated = (auth: boolean) => {
    isAuthenticated.value = auth
  }

  const setAuthToken = (token: string) => {
    authToken.value = token
  }

  const setCsrfToken = (token: string) => {
    csrfToken.value = token
  }

  const getCsrfToken = async () => {
    if (csrfToken.value) {
      return csrfToken.value
    }
    const csrfTokenUrl = '/api/csrf/'
    try {
      const response = await fetch(csrfTokenUrl, {
        method: 'GET',
        credentials: 'include' // Ensure cookies are sent with the request
      })
      if (!response.ok) {
        throw new Error('Failed to fetch CSRF token')
      }
      const data = await response.json()
      return data.csrfToken
    } catch (error) {
      console.error('Error fetching CSRF token:', error)
      return null // Return null if there was an error
    }
  }
  const checkLoginStatus = async () => {
    // console.log('Checking login status')
    const csrfToken = await getCsrfToken()
    if (!csrfToken) {
      console.error('CSRF token not available')
      isInitialized.value = true  
      return
    }

    const headers = {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken
    }

    const loginStatusUrl = '/api/login/status/'
    try {
      const response = await fetch(loginStatusUrl, {
        method: 'GET',
        credentials: 'include',
        headers: headers
      })
      if (!response.ok) {
        isInitialized.value = true
        throw new Error('Failed to check login status')
      }
      const data = await response.json()
      if (data.success) {
        setUsername(data.username)
        setAuthenticated(true)
      } else {
        setUsername('')
        setAuthenticated(false)
      }
    } catch (error) {
      console.error('Error checking login status:', error)
    }
    isInitialized.value = true
  }

  const get_pk = async () => {
    const csrfToken = await getCsrfToken()
    if (!csrfToken) {
      console.error('CSRF token not available')
      return
    }
    const headers = {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken
    }
    const get_pk_url = '/api/get-user-pk/'
    try {
      const response = await fetch(get_pk_url, {
        method: 'GET',
        credentials: 'include',
        headers: headers
      })
      if (!response.ok) {
        throw new Error('Failed to get pk')
      }
      const data = await response.json()
      return data.pk
    } catch (error) {
      console.error('Error getting pk:', error)
      return null
    }
  }

  const login = async (username: string, password: string) => {
    const login_url = '/api/login/'

    // Wait for getCsrfToken to finish
    const csrfToken = await getCsrfToken()

    // If no CSRF token is available, log an error and return
    if (!csrfToken) {
      console.error('CSRF token not available')
      isInitialized.value = true
      throw new Error('CSRF token not available')
    }

    // Set the headers with the CSRF token
    const headers = {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken
    }

    // Set the body with the username and password
    const body = JSON.stringify({ username, password })

    // Make the POST request to the login endpoint
    const response = await fetch(login_url, {
      method: 'POST',
      headers,
      body,
      credentials: 'include'
    })

    if (response.ok) {
      const data = await response.json()
      if (!data.success) {
        isInitialized.value = true
        throw new Error('Unhandled login error: ' + data)
      }
      console.log('Login successful')
      console.log('Redirecting to: ' + redirectOnSignIn.value)
      setUsername(data.username)
      setAuthenticated(true)
      if (redirectOnSignIn.value) {
        console.log('Redirecting to: ' + redirectOnSignIn.value)
        router.push(redirectOnSignIn.value)
        redirectOnSignIn.value = ''
      }
    }
    isInitialized.value = true
    return response
  }

  const logout = async () => {
    const logout_url = '/api/logout/'
    const csrfToken = await getCsrfToken()
    if (!csrfToken) {
      console.error('CSRF token not available')
      return
    }
    const headers = {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken
    }
    try {
      const response = await fetch(logout_url, {
        method: 'POST',
        credentials: 'include',
        headers: headers
      })
      if (!response.ok) {
        throw new Error('Failed to logout')
      }
      setUsername('')
      setAuthenticated(false)

      // redirect to the home page
      router.push('/')
    } catch (error) {
      console.error('Error logging out:', error)
    }
  }

  return {
    login,
    logout,
    checkLoginStatus,
    username,
    isAuthenticated,
    authToken,
    csrfToken,
    setUsername,
    setAuthenticated,
    setAuthToken,
    setCsrfToken,
    getCsrfToken,
    loginDialogVisible,
    get_pk,
    redirectOnSignIn,
    isInitialized
  }
})
